import React from 'react';
import { styled, css } from '@compiled/react';
import Heading from '@atlaskit/heading';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import { token } from '@atlaskit/tokens';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import commonMessages from '../../../common/messages.tsx';
import { TrendSummary } from '../../../common/ui/trend-summary/index.tsx';
import { useFormatDurationFromSeconds } from '../../../common/utils/date-range.tsx';
import type { BarChartBarData } from '../../../services/historical-cycle-time/types.tsx';
import messages from './messages.tsx';

type Props = {
	hasPermissionForAllContributingIssues: boolean;
	selectedBarData: BarChartBarData;
	wholePeriodStatistics: number | null;
};

export const SelectionDetail = ({
	selectedBarData,
	wholePeriodStatistics,
	hasPermissionForAllContributingIssues,
}: Props) => {
	const { formatDate, formatMessage } = useIntl();
	const formatDurationFromSeconds = useFormatDurationFromSeconds();
	if (selectedBarData.cycleTimeStatistics === null) return null;

	const durationMessage = formatDurationFromSeconds(selectedBarData.cycleTimeStatistics, {
		days: 'long',
		hours: 'short',
	});
	return (
		<Wrapper>
			<Header>
				<Heading size="xxsmall">
					<WeekStartText>
						{formatMessage(messages.weekStartDate, {
							date: formatDate(selectedBarData.startDateInclusive, {
								day: 'numeric',
								month: 'short',
							}),
						})}
					</WeekStartText>
				</Heading>
			</Header>
			<Heading
				size="medium"
				testId="software-cycle-time-breakdown-report.ui.historical-cycle-time.selection-detail.title"
			>
				{durationMessage}
				<MedianCycleTime>{formatMessage(commonMessages.medianCycleTime)}</MedianCycleTime>
			</Heading>

			{hasPermissionForAllContributingIssues && (
				<IssuesShippedWrapper>
					<IssuesIcon LEGACY_size="small" label="" />
					<IssueShippedText>
						{fg('jira-issue-terminology-refresh-m3')
							? formatMessage(messages.issueShippedIssueTermRefresh, {
									numIssues: selectedBarData.numIssues,
								})
							: formatMessage(messages.issueShipped, {
									numIssues: selectedBarData.numIssues,
								})}
					</IssueShippedText>
				</IssuesShippedWrapper>
			)}
			{wholePeriodStatistics !== null && selectedBarData.cycleTimeStatistics !== null && (
				<TrendSummary
					currentDuration={selectedBarData.cycleTimeStatistics}
					overallDuration={wholePeriodStatistics}
				/>
			)}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	height: '96px',
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
});

const headerBaseStyles = css({
	display: 'flex',
	height: token('space.200'),
	alignItems: 'center',
	marginTop: token('space.150'),
	marginBottom: token('space.075'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Header = styled.div(headerBaseStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WeekStartText = styled.span({
	color: token('color.text'),
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const MedianCycleTime = styled.p({
	font: token('font.body.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssuesShippedWrapper = styled.div({
	display: 'flex',
	gap: token('space.050'),
	height: token('space.200'),
	alignItems: 'center',
	marginBottom: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const IssueShippedText = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text'),
	font: token('font.body.small'),
});
